/**
 * Media Gallery Colour Filter
 * 
 * Extends Dawn's media-gallery to filter images based on selected colour variant.
 * Uses ALT text convention: "Description | Colour Name" to match images to colours.
 * 
 * KEY APPROACH:
 * - All images are rendered in the DOM by Liquid (hidden with display:none initially if not matching)
 * - This JS shows/hides items by toggling display property
 * - Falls back to showing ALL images when no ALT text matches are found
 * 
 * Listens to Dawn's pub/sub events for variant changes and dynamically
 * updates the gallery to show only images matching the selected colour.
 */

class MediaGalleryColourFilter {
  constructor() {
    this.init();
  }

  init() {
    // Find all galleries that have our JSON data block (indicating the setting is enabled)
    // We check for the data block rather than data-colour-filtering attribute
    // because filtering might be "inactive" initially but we still need to handle variant changes
    const dataBlocks = document.querySelectorAll('script[id^="ProductMediaData-"]');
    
    if (dataBlocks.length === 0) {
      console.log('[ColourFilter] No media data blocks found - colour filtering not enabled');
      return;
    }

    dataBlocks.forEach(dataBlock => {
      const sectionId = dataBlock.id.replace('ProductMediaData-', '');
      const gallery = document.querySelector(`media-gallery[data-section-id="${sectionId}"]`);
      
      if (gallery) {
        this.initGallery(gallery, dataBlock);
      }
    });
  }

  initGallery(gallery, dataElement) {
    const sectionId = gallery.dataset.sectionId;

    try {
      const mediaData = JSON.parse(dataElement.textContent);
      
      // Store reference on gallery element for later access
      gallery.mediaData = mediaData;
      gallery.allMedia = mediaData.media;
      gallery.colourOptionIndex = mediaData.colourOptionIndex;
      gallery.variants = mediaData.variants;
      
      console.log('[ColourFilter] Initialised gallery:', {
        sectionId,
        mediaCount: mediaData.media.length,
        colourOptionIndex: mediaData.colourOptionIndex,
        selectedColour: mediaData.selectedColour,
        filteringEnabled: mediaData.filteringEnabled
      });

      // Subscribe to variant selection changes
      this.subscribeToVariantChanges(gallery, sectionId);
      
    } catch (error) {
      console.error('[ColourFilter] Error parsing media data:', error);
    }
  }

  subscribeToVariantChanges(gallery, sectionId) {
    // Dawn uses a pub/sub system - subscribe to variantChange
    // IMPORTANT: We use variantChange (not optionValueSelectionChange) because:
    // - optionValueSelectionChange fires BEFORE Dawn's async fetch completes
    // - Dawn's updateMedia() then calls setActiveMedia() which overwrites our filter
    // - variantChange fires AFTER Dawn has finished all media manipulation
    // This ensures our filter runs last and "wins"
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.handleVariantChange(gallery, event);
      });
      
      console.log('[ColourFilter] Subscribed to variantChange');
    } else {
      console.warn('[ColourFilter] Dawn pub/sub not available, using fallback');
      this.setupFallbackListeners(gallery, sectionId);
    }
  }

  setupFallbackListeners(gallery, sectionId) {
    const variantSelects = document.querySelector(`variant-selects[data-section="${sectionId}"]`);
    
    if (variantSelects) {
      variantSelects.addEventListener('change', () => {
        setTimeout(() => {
          const selectedColour = this.getSelectedColourFromDOM(gallery, sectionId);
          if (selectedColour) {
            this.filterGalleryByColour(gallery, selectedColour);
          }
        }, 50);
      });
    }
  }

  handleVariantChange(gallery, event) {
    const eventSectionId = event?.data?.sectionId;
    const gallerySectionId = gallery.dataset.sectionId;
    
    // Only process if this event is for our gallery's section
    if (eventSectionId && eventSectionId !== gallerySectionId) {
      return;
    }

    // Get the selected colour from the event or DOM
    const selectedColour = this.extractColourFromEvent(gallery, event);
    
    if (selectedColour) {
      console.log('[ColourFilter] Selected colour:', selectedColour);
      this.filterGalleryByColour(gallery, selectedColour);
    }
  }

  extractColourFromEvent(gallery, event) {
    const colourOptionIndex = gallery.colourOptionIndex;
    
    if (colourOptionIndex === null || colourOptionIndex === undefined) {
      console.log('[ColourFilter] No colour option index configured');
      return null;
    }

    // Try to get colour from event data
    if (event?.data?.variant?.options) {
      return event.data.variant.options[colourOptionIndex];
    }

    // Fallback: Get from DOM
    return this.getSelectedColourFromDOM(gallery, gallery.dataset.sectionId);
  }

  /**
   * Fallback method to get the selected colour from the DOM when event data is unavailable.
   *
   * IMPORTANT: This method uses a CLASS SELECTOR (not index) to find the colour fieldset.
   *
   * Previously, this used: fieldsets[colourOptionIndex]
   * This broke when we changed the variant option render order because:
   * - colourOptionIndex comes from product.options_with_values (Shopify's data order)
   * - But the DOM fieldsets were rendered in a different order (colour first/last)
   *
   * The class-based approach is ORDER-AGNOSTIC - it finds the colour fieldset regardless
   * of where it appears in the DOM. The 'product-form__input--colour' class is added by
   * cosy-variant-option-field.liquid when is_colour_option is true.
   *
   * See docs/colour-filter-debugging-notes.md for full debugging history.
   */
  getSelectedColourFromDOM(gallery, sectionId) {
    const variantPicker = document.querySelector(`variant-selects[data-section="${sectionId}"]`);

    if (!variantPicker) {
      return null;
    }

    const colourFieldset = variantPicker.querySelector('fieldset.product-form__input--colour');

    if (!colourFieldset) {
      return null;
    }

    const checkedInput = colourFieldset.querySelector('input:checked');

    return checkedInput ? checkedInput.value : null;
  }

  filterGalleryByColour(gallery, colour) {
    const colourLower = colour.toLowerCase();
    const allMedia = gallery.allMedia;
    
    // Filter media by ALT text
    const matchingMedia = allMedia.filter(media => {
      if (!media.alt) return false;
      
      const altLower = media.alt.toLowerCase();
      
      // Check for pipe delimiter format: "Description | Colour"
      if (altLower.includes('|')) {
        const parts = altLower.split('|');
        const colourPart = parts[parts.length - 1].trim();
        return colourPart === colourLower;
      }
      
      // Fallback: Check if colour appears anywhere in alt text
      return altLower.includes(colourLower);
    });

    console.log('[ColourFilter] Filtering results:', {
      colour,
      totalMedia: allMedia.length,
      matchingMedia: matchingMedia.length
    });

    // If no matches, fall back to showing all media
    const mediaToShow = matchingMedia.length > 0 ? matchingMedia : allMedia;
    
    if (matchingMedia.length === 0) {
      console.log('[ColourFilter] No matching media found, showing all images (fallback)');
    }

    // Update the gallery display
    this.updateGalleryDisplay(gallery, mediaToShow, allMedia);
    
    // Also update the modal
    this.updateModalDisplay(gallery, colourLower, matchingMedia.length > 0);
  }

  updateGalleryDisplay(gallery, mediaToShow, allMedia) {
    const sectionId = gallery.dataset.sectionId;
    
    // Get the main slider and thumbnail slider
    const mainSlider = gallery.querySelector(`#Slider-Gallery-${sectionId}`);
    const thumbnailSlider = gallery.querySelector(`#Slider-Thumbnails-${sectionId}`);
    
    if (!mainSlider) {
      console.error('[ColourFilter] Main slider not found');
      return;
    }

    // Create a Set of IDs to show for quick lookup
    const idsToShow = new Set(mediaToShow.map(m => m.id.toString()));
    
    // IMPORTANT: Use our data order (mediaToShow) as source of truth for first image
    // Dawn's setActiveMedia() physically reorders DOM elements, so we can't rely on
    // DOM iteration order. mediaToShow preserves the original Liquid/Shopify order.
    const firstMediaId = mediaToShow[0]?.id?.toString();

    // Update main gallery items visibility
    const mainItems = mainSlider.querySelectorAll('.product__media-item');
    
    mainItems.forEach(item => {
      // Extract the media ID from the element's id attribute
      // Format: "Slide-{sectionId}-{mediaId}"
      const itemId = item.id;
      const mediaIdMatch = itemId.match(/-(\d+)$/);
      const mediaId = mediaIdMatch ? mediaIdMatch[1] : null;
      
      const shouldShow = mediaId && idsToShow.has(mediaId);
      
      // Toggle visibility
      item.style.display = shouldShow ? '' : 'none';
      
      // Remove is-active from all, we'll set the correct one after
      item.classList.remove('is-active');
    });

    // IMPORTANT: Reorder main gallery items to match our data order
    // Dawn's setActiveMedia() physically reorders DOM elements (prepend),
    // which breaks mobile slider (shows first DOM item, not active item).
    // By reordering to match our data order, the first visible item in DOM
    // will be our intended first image.
    mediaToShow.forEach(media => {
      const mediaId = media.id.toString();
      const item = mainSlider.querySelector(`[id$="-${mediaId}"]`);
      if (item) {
        // Appending in order effectively sorts by our data order
        mainSlider.appendChild(item);
      }
    });

    // Set the first image as active (now it's actually first in DOM too)
    if (firstMediaId) {
      const firstItem = mainSlider.querySelector(`[id$="-${firstMediaId}"]`);
      if (firstItem) {
        firstItem.classList.add('is-active');
      }
    }

    // Reset scroll position to show first item (important for mobile slider)
    // Use setTimeout to ensure we run AFTER any Dawn animations/transitions
    // and ResizeObserver callbacks triggered by our DOM reordering
    setTimeout(() => {
      mainSlider.scrollLeft = 0;
      // Recount the slides for the mobile "1 / 9" counter and arrows. Dawn only recounts when
      // the slider is resized, and hiding or reordering slides doesn't resize it.
      mainSlider.closest('slider-component')?.resetPages?.();
    }, 50);
    
    // Set thumbnail aria-current after Dawn's debounced slideChanged handler
    // Dawn debounces at 500ms, so we use 600ms to ensure we run last
    setTimeout(() => {
      if (thumbnailSlider && firstMediaId) {
        thumbnailSlider.querySelectorAll('button').forEach(btn => {
          btn.setAttribute('aria-current', 'false');
        });
        const firstThumb = thumbnailSlider.querySelector(`[id$="-${firstMediaId}"]`);
        if (firstThumb) {
          const button = firstThumb.querySelector('button');
          if (button) {
            button.setAttribute('aria-current', 'true');
          }
        }
      }
    }, 600);

    // Update thumbnail items visibility
    if (thumbnailSlider) {
      const thumbnailItems = thumbnailSlider.querySelectorAll('.thumbnail-list__item');
      
      thumbnailItems.forEach(item => {
        // Get media ID from the item's id attribute
        const itemId = item.id;
        const mediaIdMatch = itemId.match(/-(\d+)$/);
        const mediaId = mediaIdMatch ? mediaIdMatch[1] : null;
        
        const shouldShow = mediaId && idsToShow.has(mediaId);
        
        item.style.display = shouldShow ? '' : 'none';
        
        // Reset aria-current on all thumbnail buttons
        const button = item.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', 'false');
        }
      });

      // IMPORTANT: Reorder visible thumbnails to match our data order
      // Dawn's setActiveMedia() physically reorders thumbnail DOM elements,
      // so we need to restore our intended order.
      // NOTE: thumbnailSlider IS the UL.thumbnail-list element, so we append directly to it
      mediaToShow.forEach(media => {
        const mediaId = media.id.toString();
        const thumb = thumbnailSlider.querySelector(`[id$="-${mediaId}"]`);
        if (thumb) {
          // Appending in order effectively sorts by our data order
          thumbnailSlider.appendChild(thumb);
        }
      });

      // Set the first thumbnail as current (now it's actually first in DOM too)
      if (firstMediaId) {
        const firstThumb = thumbnailSlider.querySelector(`[id$="-${firstMediaId}"]`);
        if (firstThumb) {
          const button = firstThumb.querySelector('button');
          if (button) {
            button.setAttribute('aria-current', 'true');
          }
        }
      }
    }

    // Update the counter
    this.updateCounter(gallery, mediaToShow.length);

    // Announce change to screen readers
    const status = gallery.querySelector(`#GalleryStatus-${sectionId}`);
    if (status) {
      status.textContent = `Showing ${mediaToShow.length} images`;
    }

    console.log('[ColourFilter] Gallery updated, showing', mediaToShow.length, 'images');
  }

  updateCounter(gallery, count) {
    const counter = gallery.querySelector('.slider-counter--total');
    if (counter) {
      counter.textContent = count;
    }
    
    // Reset current position
    const currentCounter = gallery.querySelector('.slider-counter--current');
    if (currentCounter) {
      currentCounter.textContent = '1';
    }
  }

  /**
   * Update the product media modal to show/hide images based on colour
   * @param {HTMLElement} gallery - The gallery element (used to get section ID)
   * @param {string} colourLower - The selected colour (lowercase)
   * @param {boolean} hasMatches - Whether any images matched the colour filter
   */
  updateModalDisplay(gallery, colourLower, hasMatches) {
    const sectionId = gallery.dataset.sectionId;
    const modal = document.querySelector(`#ProductModal-${sectionId}`);
    
    if (!modal) {
      console.log('[ColourFilter] Modal not found for section:', sectionId);
      return;
    }

    const modalItems = modal.querySelectorAll('.product-media-modal__item');
    
    if (modalItems.length === 0) {
      console.log('[ColourFilter] No modal items found');
      return;
    }

    let visibleCount = 0;

    modalItems.forEach(item => {
      const colourTag = item.dataset.colourTag?.toLowerCase() || '';
      
      // Determine visibility:
      // - If we have matches, only show items with matching colour tag
      // - If no matches (fallback mode), show all items
      let shouldShow = false;
      
      if (!hasMatches) {
        // Fallback: show all
        shouldShow = true;
      } else if (colourTag === colourLower) {
        // Has matches and this item matches
        shouldShow = true;
      }
      
      item.style.display = shouldShow ? '' : 'none';
      
      if (shouldShow) {
        visibleCount++;
      }
    });

    console.log('[ColourFilter] Modal updated, showing', visibleCount, 'images');
  }
}

// Initialise when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.mediaGalleryColourFilter = new MediaGalleryColourFilter();
  });
} else {
  window.mediaGalleryColourFilter = new MediaGalleryColourFilter();
}
